import { MutableRefObject, useCallback, useEffect, useRef, useState } from "react";
import { useUpdateTruckingRoute } from "./useUpdateTruckingRoute";
import { TruckingGuestRoutePointDto } from "../dtos/ShipmentTruckingGuestInfoResponseDto";
import { CommonMessage } from "../../../base/components/message/CommonMessage";

export type CheckinState = "IDLE" | "CHECKED_IN" | "CHECKED_OUT";

export const mapStatusToCheckinState = (status: number): CheckinState => {
  if (status === 1) return "CHECKED_IN";
  if (status === 2) return "CHECKED_OUT";
  return "IDLE";
};

interface UseCheckinCheckoutOptions {
  shipmentTruckingId: string | null | undefined;
  currentRoutePoint: TruckingGuestRoutePointDto | null | undefined;
  isWithinGeofence: boolean;
  geoReady: boolean;
  coords: [number, number] | null;
  isLast: boolean;
  isMountedRef: MutableRefObject<boolean>;
  onAdvance: () => void;
}

interface UseCheckinCheckoutResult {
  checkinState: CheckinState;
  checkinTime: string | null;
  eventLoading: boolean;
  manualCheckin: () => void;
  manualCheckout: () => void;
}

export const useCheckinCheckout = ({
  shipmentTruckingId,
  currentRoutePoint,
  isWithinGeofence,
  geoReady,
  coords,
  isMountedRef,
  onAdvance,
}: UseCheckinCheckoutOptions): UseCheckinCheckoutResult => {
  const { mutateAsync: updateRoute, isPending: routeUpdating } = useUpdateTruckingRoute();

  const getInitialState = () => mapStatusToCheckinState(currentRoutePoint?.status ?? 0);

  const [checkinState, setCheckinState] = useState<CheckinState>(getInitialState);
  const [checkinTime, setCheckinTime] = useState<string | null>(null);

  const pendingRef = useRef(false);
  const prevWithinRef = useRef<boolean | null>(null);
  const checkinStateRef = useRef<CheckinState>(getInitialState());

  // Giữ coords mới nhất qua ref — tránh postEvent recreate mỗi lần GPS tick
  const coordsRef = useRef(coords);
  useEffect(() => {
    coordsRef.current = coords;
  }, [coords]);

  useEffect(() => {
    checkinStateRef.current = checkinState;
  }, [checkinState]);

  useEffect(() => {
    if (!currentRoutePoint) return;
    const restored = mapStatusToCheckinState(currentRoutePoint.status);
    setCheckinState(restored);
    checkinStateRef.current = restored;
    prevWithinRef.current = null;
    pendingRef.current = false;
    setCheckinTime(null);
  }, [currentRoutePoint]);

  const postEvent = useCallback(
    async (eventType: "CHECK_IN" | "CHECK_OUT") => {
      if (!shipmentTruckingId || !currentRoutePoint || !coordsRef.current) return;
      if (pendingRef.current) return;

      pendingRef.current = true;
      const timestamp = new Date().toISOString();
      const [lat, lng] = coordsRef.current;

      try {
        await updateRoute({ shipmentTruckingId, payload: { lat, lng } });

        if (!isMountedRef.current) return;

        if (eventType === "CHECK_IN") {
          setCheckinTime(timestamp);
          setCheckinState("CHECKED_IN");
          checkinStateRef.current = "CHECKED_IN";
          CommonMessage.success(`✓ Check-in: ${currentRoutePoint.stopTypeName} — ${new Date(timestamp).toLocaleTimeString("vi-VN")}`);
        } else {
          setCheckinState("CHECKED_OUT");
          checkinStateRef.current = "CHECKED_OUT";
          CommonMessage.success(`✓ Check-out: ${currentRoutePoint.stopTypeName}`);
          if (!isMountedRef.current) return;
          onAdvance();
        }
      } catch (err: any) {
        if (!isMountedRef.current) return;
        CommonMessage.error(err?.response?.data?.message ?? "Không ghi được vị trí. Thử lại sau.");
      } finally {
        pendingRef.current = false;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shipmentTruckingId, currentRoutePoint?.id, isMountedRef, onAdvance, updateRoute],
  );

  // Auto check-in / check-out theo geofence
  useEffect(() => {
    if (!geoReady || !currentRoutePoint) return;

    const prev = prevWithinRef.current;
    prevWithinRef.current = isWithinGeofence;

    // ── Lần đầu GPS sẵn sàng
    if (prev === null) {
      // App tắt giữa chừng, driver đã rời đi → CHECK_OUT ngay
      if (checkinStateRef.current === "CHECKED_IN" && !isWithinGeofence) {
        postEvent("CHECK_OUT");
      }
      // Driver đứng sẵn trong bán kính khi GPS khởi động → CHECK_IN ngay
      if (checkinStateRef.current === "IDLE" && isWithinGeofence) {
        postEvent("CHECK_IN");
      }
      return; // ← một return duy nhất, không còn block thứ 2
    }

    // ── Các lần sau: chỉ fire khi có transition 
    const state = checkinStateRef.current;

    // Vừa bước vào bán kính
    if (state === "IDLE" && !prev && isWithinGeofence) {
      postEvent("CHECK_IN");
      return;
    }

    // Vừa bước ra khỏi bán kính
    if (state === "CHECKED_IN" && prev && !isWithinGeofence) {
      postEvent("CHECK_OUT");
    }
  }, [isWithinGeofence, geoReady, currentRoutePoint, postEvent]);

  // Manual handlers — gọi trực tiếp postEvent, bỏ qua geofence guard
  const manualCheckin = useCallback(() => {
    if (checkinStateRef.current !== "IDLE") return;
    postEvent("CHECK_IN");
  }, [postEvent]);

  const manualCheckout = useCallback(() => {
    if (checkinStateRef.current !== "CHECKED_IN") return;
    postEvent("CHECK_OUT");
  }, [postEvent]);

  return { checkinState, checkinTime, eventLoading: routeUpdating, manualCheckin, manualCheckout };
};
